import { Spotlight, SpotlightCard } from '@atlaskit/onboarding';
import { useInteractiveBoardStore } from '../state';
import { useEffect, useRef, useState } from 'react';
import { FIRST_ISSUE_ID, Steps } from '../constants/ui';
import { adsFont, spotlightHeader } from './spotlight-manager.styles';
import { useSpotlightManager } from './spotlight-provider';

type SpotlightProps = React.ComponentProps<typeof Spotlight> & {
  description: string;
  key: string;
  step: Steps;
  topOffset?: number;
  leftOffset?: number;
};
export const SpotlightManager = () => {
  const [{ isDemoMessageVisible, messages, step }, { setSpotlightVisibility }] =
    useInteractiveBoardStore();

  const spotlightCardRef = useRef<HTMLDivElement | null>(null);

  const [spotlightPosition, setSpotlightPosition] = useState<{
    top: string | undefined;
    left: string | undefined;
  }>({
    top: undefined,
    left: undefined
  });

  const [spotlight, setSpotlight] = useState<SpotlightProps | undefined>(
    undefined
  );

  const { targets, setCurrent } = useSpotlightManager();

  useEffect(() => {
    // make sure spotlight is visible again if a new step is encountered.
    void setSpotlightVisibility(true);

    switch (step) {
      case Steps.CREATE_FIRST_STORY:
        setSpotlight({
          step: Steps.CREATE_FIRST_STORY,
          heading: messages.spotlightInitialCreateButtonHeading,
          description: messages.spotlightInitialCreateButtonDescription,
          target: 'openIssueModalButton',
          key: 'openIssueModalButton',
          leftOffset: -278,
          topOffset: 10
        });
        break;
      case Steps.SELECT_ISSUE_TYPE:
        setSpotlight({
          step: Steps.SELECT_ISSUE_TYPE,
          heading: messages.spotlightSelectIssueHeading,
          description: messages.spotlightSelectIssueDescription,
          target: 'issueType',
          key: 'issueType',
          leftOffset: 28
        });
        break;
      case Steps.ADD_SUMMARY:
        setSpotlight({
          step: Steps.ADD_SUMMARY,
          heading: messages.spotlightSummaryHeading,
          description: messages.spotlightSummaryDescription,
          target: 'summary',
          key: 'summary'
        });
        break;
      case Steps.ADD_DESCRIPTION:
        setSpotlight({
          step: Steps.ADD_DESCRIPTION,
          heading: messages.spotlightAddDescriptionHeading,
          description: messages.spotlightAddDescriptionDescription,
          target: 'issueDescription',
          key: 'issueDescription'
        });
        break;
      case Steps.SELECT_PRIORITY:
        setSpotlight({
          step: Steps.SELECT_PRIORITY,
          heading: messages.spotlightSelectPriorityHeading,
          description: messages.spotlightSelectPriorityDescription,
          target: 'priority',
          key: 'priority'
        });
        break;
      case Steps.SELECT_EPIC_LINK:
        setSpotlight({
          step: Steps.SELECT_EPIC_LINK,
          heading: messages.spotlightSelectEpicLinkHeading,
          description: messages.spotlightSelectEpicLinkDescription,
          target: 'epicLink',
          key: 'epicLink'
        });
        break;
      case Steps.MOVE_ISSUE:
        setSpotlight({
          step: Steps.MOVE_ISSUE,
          heading: messages.spotlightMoveIssueHeading,
          description: messages.spotlightMoveIssueDescription,
          target: 'create',
          key: 'create',
          leftOffset: -310,
          topOffset: -163
        });
        break;
      case Steps.PULSE_CARD:
        void setSpotlightVisibility(false);
        setSpotlight({
          step: Steps.PULSE_CARD,
          description: '',
          target: FIRST_ISSUE_ID,
          key: FIRST_ISSUE_ID
        });
        break;
      default:
        console.warn('this step does not have a defined spotlight');
        void setSpotlightVisibility(false);
        setSpotlight(undefined);
    }
  }, [step, messages]);

  useEffect(() => {
    if (spotlight && targets) {
      const elementRef = targets.get(spotlight.target ?? '');

      if (elementRef?.current) {
        setCurrent && setCurrent(elementRef); // set current ref
      }

      // set card relative to current step element
      if (elementRef?.current && spotlightCardRef.current) {
        // additional padding to allow custom offset relative to target element
        const {
          topOffset: additionalTopOffset = 0,
          leftOffset: additionalLeftOffset = 0
        } = spotlight;

        const paddingTop = 10; // represents padding above spotlight relative to the bottom of the target element
        const paddingLeft = 60; // represents padding to the left of the spotlight.

        const {
          offsetLeft: targetElementLeftOffset, // the target elements current position from the left relative to it's parent.
          offsetTop: targetElementTopOffset, // the target elements current position from the top relative to it's parent.
          offsetHeight: targetElementHeight // the target elements height
        } = elementRef.current;

        const spotlightTopOffset =
          targetElementTopOffset +
          targetElementHeight +
          additionalTopOffset +
          paddingTop +
          'px';

        const spotlightLeftOffset =
          targetElementLeftOffset + additionalLeftOffset + paddingLeft + 'px';

        setSpotlightPosition({
          top: spotlightTopOffset,
          left: spotlightLeftOffset
        });
      }
    } else {
      setCurrent && setCurrent(null);
    }
  }, [spotlight]);

  if (!isDemoMessageVisible) {
    return;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: spotlightPosition.top,
        left: spotlightPosition.left
      }}
    >
      {spotlight && (
        <SpotlightCard
          ref={spotlightCardRef}
          width={301}
          actionsBeforeElement={
            <div css={adsFont}>{`${spotlight.step + 1}/7`}</div>
          }
          key={spotlight.key}
        >
          <div css={spotlightHeader}>{spotlight.heading}</div>
          <div css={adsFont}>{spotlight.description}</div>
        </SpotlightCard>
      )}
    </div>
  );
};
